// src/components/FeaturedCoursesSection.jsx
import { useEffect, useState } from "react";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";
import PortalIcon from "./ui/PortalIcon";

const MASTER_COURSE = {
  title: "Smart Teachers Mastery Course",
  subtitle: "Complete Digital Teaching Bundle",
  description:
    "Master PPT designing, fast typing, thumbnail creation, AI tools, YouTube branding, video editing, and monetization - everything a modern teacher needs.",
  price: 9999,
  mrp: 49999,
  duration: "30+ hrs",
  level: "Beginner Friendly",
  students: "500+",
  isBestseller: true,
};

function money(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN")}`;
}

function SectionBadge({ children, t }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        background: `${t.gold}14`,
        border: `1px solid ${t.gold}35`,
        color: t.gold,
        fontSize: ".78rem",
        fontWeight: 900,
        textTransform: "uppercase",
        padding: "6px 14px",
        borderRadius: 999,
        marginBottom: 16,
      }}
    >
      {children}
    </div>
  );
}

function PrimaryButton({ children, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        fontWeight: 900,
        textDecoration: "none",
        borderRadius: 12,
        cursor: "pointer",
        border: "none",
        fontFamily: "inherit",
        background: "linear-gradient(135deg,#F59E0B,#F97316)",
        color: "#070B14",
        padding: "14px 28px",
        fontSize: "1rem",
        boxShadow: "0 12px 28px rgba(245,158,11,.28)",
      }}
    >
      {children}
    </button>
  );
}

function GhostButton({ children, onClick, t }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        fontWeight: 850,
        textDecoration: "none",
        borderRadius: 12,
        cursor: "pointer",
        border: `1px solid ${t.border}`,
        fontFamily: "inherit",
        background: t.bgCard,
        color: t.text,
        padding: "14px 24px",
        fontSize: ".96rem",
      }}
    >
      {children}
    </button>
  );
}

function MasterCourseSection({ t, setPage }) {
  return (
    <section
      style={{
        padding: "clamp(60px,8vw,84px) clamp(16px,5%,60px)",
        textAlign: "center",
        background: t.bgCard2 || t.bg,
        position: "relative",
        overflow: "hidden",
        borderTop: `1px solid ${t.border}`,
        borderBottom: `1px solid ${t.border}`,
      }}
    >
      <div style={{ position: "absolute", top: -120, right: -90, width: 320, height: 320, background: `radial-gradient(circle, ${t.gold}55 0%, transparent 70%)`, filter: "blur(70px)" }} />
      <div style={{ position: "relative", zIndex: 1, maxWidth: 650, margin: "0 auto" }}>
        <SectionBadge t={t}><PortalIcon name="flame" size={15} />High Value Course</SectionBadge>
        <h2 style={{ fontSize: "clamp(1.8rem,4vw,2.8rem)", fontWeight: 950, color: t.text, margin: "0 0 14px", lineHeight: 1.18 }}>
          {MASTER_COURSE.title}
        </h2>
        <p style={{ color: t.muted, fontSize: "1rem", margin: "0 auto 24px", lineHeight: 1.7, maxWidth: 560 }}>
          {MASTER_COURSE.description}
        </p>
        <div style={{ marginBottom: 24 }}>
          <span style={{ fontSize: ".9rem", color: t.muted, textDecoration: "line-through" }}>{money(MASTER_COURSE.mrp)}</span>
          <div style={{ fontSize: "3rem", fontWeight: 950, color: t.gold, lineHeight: 1 }}>{money(MASTER_COURSE.price)}</div>
          <div style={{ fontSize: ".84rem", color: t.green, fontWeight: 850, marginTop: 6 }}>
            Save {money(MASTER_COURSE.mrp - MASTER_COURSE.price)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <PrimaryButton onClick={() => setPage("landing")}>Join Course Now</PrimaryButton>
          <GhostButton onClick={() => setPage("landing")} t={t}>Learn More</GhostButton>
        </div>
      </div>
    </section>
  );
}

function CourseCard({ course, t, setPage, delay = 0 }) {
  const [hovered, setHovered] = useState(false);
  const price = Number(course.price || 0);
  const mrp = Number(course.mrp || 0);
  const hasDiscount = mrp > price && price > 0;

  return (
    <article
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => setPage("courses")}
      style={{
        background: t.bgCard,
        border: `1px solid ${hovered ? t.gold : t.border}`,
        borderRadius: 18,
        overflow: "hidden",
        cursor: "pointer",
        transform: hovered ? "translateY(-6px)" : "translateY(0)",
        boxShadow: hovered ? `0 22px 48px ${t.shadow || "rgba(0,0,0,.18)"}` : `0 12px 30px ${t.shadow || "rgba(0,0,0,.08)"}`,
        transition: "transform .25s ease, border-color .25s ease, box-shadow .25s ease",
        animation: `fadeInUp .6s ease ${delay}s both`,
        display: "flex",
        flexDirection: "column",
        minHeight: "100%",
      }}
    >
      <div
        style={{
          height: 190,
          background: course.thumbnail ? t.bgCard2 : `linear-gradient(135deg,${t.accent},${t.gold})`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {course.thumbnail ? (
          <img
            src={course.thumbnail}
            alt={course.title || "Course"}
            style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform .35s ease", transform: hovered ? "scale(1.04)" : "scale(1)" }}
          />
        ) : (
          <div style={{ height: "100%", display: "grid", placeItems: "center", color: "#fff" }}><PortalIcon name="book" size={48} /></div>
        )}

        {course.isBestseller && (
          <div style={{ position: "absolute", top: 14, right: 14, background: "linear-gradient(135deg,#EF4444,#F97316)", color: "#fff", padding: "6px 12px", borderRadius: 999, fontSize: ".72rem", fontWeight: 900 }}>
            Bestseller
          </div>
        )}
      </div>

      <div style={{ padding: "22px 24px", display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ display: "inline-flex", alignSelf: "flex-start", alignItems: "center", gap: 8, background: `${t.accent}12`, border: `1px solid ${t.accent}30`, color: t.accent, fontSize: ".75rem", fontWeight: 900, textTransform: "uppercase", padding: "6px 12px", borderRadius: 999, marginBottom: 14 }}>
          <PortalIcon name="book" size={14} />Featured Course
        </div>

        <h3 style={{ fontSize: "1.35rem", fontWeight: 950, color: t.text, margin: "0 0 10px", lineHeight: 1.22 }}>
          {course.title || "Untitled Course"}
        </h3>
        <div style={{ fontSize: ".92rem", color: t.muted, marginBottom: 14, fontWeight: 650 }}>
          {course.subtitle || course.category || "Self-paced learning"}
        </div>
        <p style={{ fontSize: ".92rem", color: t.muted, lineHeight: 1.65, margin: "0 0 20px", flex: 1 }}>
          {(course.description || "Learn practical skills with DG HelpMate.").slice(0, 150)}
          {(course.description || "").length > 150 ? "..." : ""}
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 20, padding: 14, background: t.bg, borderRadius: 14, border: `1px solid ${t.border}` }}>
          {[
            ["Duration", course.duration || "Self-paced"],
            ["Level", course.level || "Beginner"],
            ["Students", course.students || "500+"],
          ].map(([label, value]) => (
            <div key={label} style={{ textAlign: "center", minWidth: 0 }}>
              <div style={{ fontSize: ".72rem", color: t.muted, marginBottom: 5 }}>{label}</div>
              <div style={{ fontSize: ".82rem", fontWeight: 850, color: t.text, overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center", marginBottom: 18 }}>
          {hasDiscount && <div style={{ fontSize: ".84rem", color: t.muted, textDecoration: "line-through", marginBottom: 4 }}>MRP: {money(mrp)}</div>}
          <div style={{ fontSize: "2rem", fontWeight: 950, color: t.gold, lineHeight: 1 }}>{money(price)}</div>
          {hasDiscount && (
            <div style={{ fontSize: ".84rem", color: t.green, fontWeight: 850, marginTop: 7, background: `${t.green}14`, padding: "4px 8px", borderRadius: 8, display: "inline-block" }}>
              Save {money(mrp - price)}
            </div>
          )}
        </div>

        <button
          onClick={(event) => {
            event.stopPropagation();
            setPage("courses");
          }}
          style={{
            width: "100%",
            borderRadius: 13,
            border: "none",
            background: "linear-gradient(135deg,#F59E0B,#F97316)",
            color: "#070B14",
            padding: "14px 22px",
            fontSize: "1rem",
            fontWeight: 950,
            cursor: "pointer",
            fontFamily: "inherit",
            boxShadow: "0 10px 24px rgba(245,158,11,.28)",
          }}
        >
          Enroll Now
        </button>
      </div>
    </article>
  );
}

export default function FeaturedCoursesSection({ t, setPage }) {
  const [featuredCourses, setFeaturedCourses] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadData() {
      try {
        const siteContentSnap = await getDoc(doc(db, "siteContent", "main"));
        const siteContent = siteContentSnap.exists() ? siteContentSnap.data() : {};
        const coursesSnap = await getDocs(collection(db, "courses"));
        const allCourses = coursesSnap.docs.map((courseDoc) => ({ id: courseDoc.id, ...courseDoc.data() }));

        if (!mounted) return;
        setCourses(allCourses);
        setFeaturedCourses(siteContent.featuredCourses || { useMasterCourse: true, courses: [] });
      } catch (error) {
        console.error("Error loading featured courses:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadData();
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) {
    return (
      <section style={{ padding: "clamp(60px,8vw,80px) clamp(16px,5%,60px)", textAlign: "center", background: t.bgCard2 || t.bg }}>
        <div style={{ minHeight: 180, display: "grid", placeItems: "center", color: t.muted }}>Loading courses...</div>
      </section>
    );
  }

  if (featuredCourses?.useMasterCourse) {
    return <MasterCourseSection t={t} setPage={setPage} />;
  }

  const featuredCourseObjects = featuredCourses?.courses
    ?.map((courseId) => courses.find((course) => course.id === courseId))
    .filter(Boolean) || [];

  if (featuredCourseObjects.length === 0) {
    return (
      <section style={{ padding: "clamp(60px,8vw,80px) clamp(16px,5%,60px)", textAlign: "center", background: t.bgCard2 || t.bg, borderTop: `1px solid ${t.border}`, borderBottom: `1px solid ${t.border}` }}>
        <div style={{ maxWidth: 580, margin: "0 auto" }}>
          <SectionBadge t={t}><PortalIcon name="book" size={15} />Featured Courses</SectionBadge>
          <h2 style={{ fontSize: "clamp(1.8rem,4vw,2.8rem)", fontWeight: 950, color: t.text, margin: "0 0 12px" }}>Coming Soon</h2>
          <p style={{ color: t.muted, fontSize: "1rem", lineHeight: 1.65 }}>No courses are currently featured. Check back soon for new offerings!</p>
        </div>
      </section>
    );
  }

  return (
    <section
      style={{
        padding: "clamp(60px,8vw,84px) clamp(16px,5%,60px)",
        background: t.bgCard2 || t.bg,
        position: "relative",
        overflow: "hidden",
        borderTop: `1px solid ${t.border}`,
        borderBottom: `1px solid ${t.border}`,
      }}
    >
      <div style={{ position: "absolute", top: -120, right: -90, width: 320, height: 320, background: `radial-gradient(circle, ${t.gold}44 0%, transparent 70%)`, filter: "blur(70px)" }} />
      <div style={{ position: "relative", zIndex: 1, maxWidth: 1180, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 42 }}>
          <SectionBadge t={t}><PortalIcon name="book" size={15} />Featured Courses</SectionBadge>
          <h2 style={{ fontSize: "clamp(1.8rem,4vw,2.8rem)", fontWeight: 950, color: t.text, margin: "0 0 12px", lineHeight: 1.18 }}>
            Popular Courses
          </h2>
          <p style={{ color: t.muted, fontSize: "1rem", lineHeight: 1.65, margin: 0 }}>
            Hand-picked courses to accelerate your teaching career.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%,320px), 1fr))", gap: 24, marginBottom: 38 }}>
          {featuredCourseObjects.map((course, index) => (
            <CourseCard key={course.id} course={course} t={t} setPage={setPage} delay={index * 0.08} />
          ))}
        </div>

        <div style={{ textAlign: "center" }}>
          <GhostButton onClick={() => setPage("courses")} t={t}>View All Courses</GhostButton>
        </div>
      </div>
    </section>
  );
}
